import React, { useEffect, useRef } from "react";
import { Box, Typography, Container, Button } from "@mui/material";
import { Fade } from "react-awesome-reveal";
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import donorImage from '../assets/images/donor.jpg';
import "../App.scss";

function Hero2() {
  const titleRef = useRef(null);

  useEffect(() => {
    const title = titleRef.current;
    if (title) {
      title.classList.add('animate-in');
    }
  }, []);

  const scrollToContent = () => {
    const element = document.querySelector('.restaurants-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const stats = [
    { icon: <RestaurantIcon />, number: "4", label: "Restaurants" },
    { icon: <LocationOnIcon />, number: "2", label: "Cities" },
    { icon: <StarIcon />, number: "5★", label: "Excellence" }
  ];

  return (
    <Box 
      className="hero-container" 
      sx={{ 
        position: 'relative', 
        overflow: 'hidden',
        backgroundImage: `url(${donorImage})`,
        backgroundSize: 'cover',
        backgroundPosition: { xs: 'center bottom', md: 'center' },
        backgroundAttachment: { xs: 'scroll', md: 'fixed' },
        backgroundRepeat: 'no-repeat',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(10, 10, 10, 0.75), rgba(20, 20, 20, 0.75)), radial-gradient(circle at 30% 50%, rgba(255, 77, 77, 0.15) 0%, rgba(0, 0, 0, 0.4) 40%, rgba(0, 0, 0, 0.7) 100%)',
          zIndex: 1,
          pointerEvents: 'none'
        }
      }}
    >
      <style>
        {`
          @keyframes floatSlow {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          @keyframes rotateSlow {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes shimmerGold {
            0%, 100% { opacity: 0.5; }
            50% { opacity: 1; }
          }
          @keyframes glow {
            0%, 100% { box-shadow: 0 0 20px rgba(255, 77, 77, 0.3); }
            50% { box-shadow: 0 0 40px rgba(255, 77, 77, 0.6); }
          }
          @keyframes slideDown {
            0% { opacity: 0; transform: translateY(-20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes bounceArrow {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(8px); }
          }
          .hero-float { animation: floatSlow 4s ease-in-out infinite; }
          .hero-rotate { animation: rotateSlow 20s linear infinite; }
          .hero-glow { animation: glow 2s ease-in-out infinite; }
          .bounce-arrow { animation: bounceArrow 2s ease-in-out infinite; }
        `}
      </style>

      {/* Animated Background Shapes */}
      <Box
        sx={{
          position: 'absolute',
          top: '-100px',
          left: '-100px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255, 77, 77, 0.1), transparent 70%)',
          filter: 'blur(80px)',
          animation: 'floatSlow 6s ease-in-out infinite',
          zIndex: 2,
          pointerEvents: 'none'
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          bottom: '-150px',
          right: '-150px',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(249, 203, 40, 0.08), transparent 70%)',
          filter: 'blur(100px)',
          animation: 'floatSlow 8s ease-in-out infinite',
          animationDelay: '-2s',
          zIndex: 2,
          pointerEvents: 'none'
        }}
      />

      <Container
        maxWidth="lg"
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 3,
          py: { xs: 6, md: 10 },
          px: { xs: 2, sm: 3, md: 4 }
        }}
      >
        <Box
          sx={{
            width: '100%',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
          ref={titleRef}
        >


          {/* Main Title */}
          <Fade direction="up" triggerOnce duration={1000}>
            <Box sx={{ mb: 2, maxWidth: '900px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.2rem', md: '4.5rem', lg: '5rem' },
                  fontWeight: 900,
                  lineHeight: 1.1,
                  mb: 1.5,
                  background: 'linear-gradient(135deg, #FF4D4D 0%, #F9CB28 50%, #FF4D4D 100%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-1px',
                  position: 'relative',
                  display: 'inline-block',
                  textShadow: '0 0 40px rgba(255, 77, 77, 0.3)',
                }}
              >
                KJ GROUP
              </Typography>
            </Box>
          </Fade>

          {/* Subtitle */}
          <Fade direction="up" triggerOnce duration={1000} delay={200}>
            <Box sx={{ mb: 2, maxWidth: '700px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.35rem' },
                  fontWeight: 300,
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: 1.8,
                  mb: 1,
                  position: 'relative'
                }}
              >
                Vitality through <Box component="span" sx={{
                  color: '#FF4D4D',
                  fontWeight: 700,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-4px',
                    left: 0,
                    width: '100%',
                    height: '2px',
                    background: 'linear-gradient(90deg, #FF4D4D, rgba(255, 77, 77, 0))',
                  }
                }}>Cuisine</Box>
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.35rem' },
                  fontWeight: 300,
                  color: 'rgba(255,255,255,0.8)',
                  lineHeight: 1.8
                }}
              >
                Unity through <Box component="span" sx={{
                  color: '#F9CB28',
                  fontWeight: 700,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-4px',
                    left: 0,
                    width: '100%',
                    height: '2px',
                    background: 'linear-gradient(90deg, #F9CB28, rgba(249, 203, 40, 0))',
                  }
                }}>Culture</Box>
              </Typography>
            </Box>
          </Fade>

         

          {/* CTA Buttons */}
          <Fade direction="up" triggerOnce duration={1000} delay={600}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mb: 5 }}>
              <Button
                onClick={scrollToContent}
                sx={{
                  background: 'linear-gradient(135deg, #FF4D4D, #F9CB28)',
                  color: '#fff',
                  padding: { xs: '12px 32px', md: '14px 40px' },
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  fontWeight: 700,
                  borderRadius: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  boxShadow: '0 8px 30px rgba(255, 77, 77, 0.3)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  border: 'none',
                  cursor: 'pointer',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                    transition: 'left 0.5s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(255, 77, 77, 0.5)',
                    '&::before': {
                      left: '100%'
                    }
                  }
                }}
              >
                Explore Now
              </Button>

              <Button
                variant="outlined"
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.4)',
                  padding: { xs: '12px 32px', md: '14px 40px' },
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  fontWeight: 700,
                  borderRadius: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(135deg, rgba(255, 77, 77, 0.2), transparent)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover': {
                    borderColor: '#FF4D4D',
                    color: '#FF4D4D',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(255, 77, 77, 0.2)',
                    '&::before': {
                      opacity: 1
                    }
                  }
                }}
                href="tel:+441313576000"
              >
                Get in Touch
              </Button>
            </Box>
          </Fade>

          {/* Stats Section */}
          <Fade direction="up" triggerOnce duration={1000} delay={800}>
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 3, md: 5 },
                flexWrap: 'wrap',
                justifyContent: 'center',
                mb: 6,
                pb: 4,
                borderBottom: '1px solid rgba(255, 77, 77, 0.2)'
              }}
            >
              {stats.map((stat, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.2,
                    position: 'relative',
                    '&:hover .stat-icon': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 40px rgba(255, 77, 77, 0.4)'
                    }
                  }}
                >
                  <Box
                    className="stat-icon"
                    sx={{
                      width: { xs: '48px', md: '56px' },
                      height: { xs: '48px', md: '56px' },
                      borderRadius: '14px',
                      background: 'linear-gradient(135deg, rgba(255, 77, 77, 0.15), rgba(249, 203, 40, 0.1))',
                      border: '1.5px solid rgba(255, 77, 77, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FF4D4D',
                      fontSize: { xs: '1.3rem', md: '1.5rem' },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      backdropFilter: 'blur(10px)',
                      position: 'relative'
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '1.4rem', md: '1.6rem' },
                        fontWeight: 800,
                        color: '#fff',
                        lineHeight: 1,
                        mb: 0.3
                      }}
                    >
                      {stat.number}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.7rem', md: '0.8rem' },
                        color: 'rgba(255,255,255,0.6)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                        fontWeight: 600
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Fade>

          {/* Scroll Indicator */}
          <Fade direction="up" triggerOnce duration={1000} delay={1000}>
            <Box
              className="bounce-arrow"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#FF4D4D'
                }
              }}
              onClick={scrollToContent}
            >
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'rgba(255,255,255,0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '1.5px',
                  fontWeight: 600
                }}
              >
                Scroll to Discover
              </Typography>
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: '1.5rem',
                  color: '#FF4D4D',
                  animation: 'bounceArrow 2s ease-in-out infinite'
                }}
              />
            </Box>
          </Fade>
        </Box>
      </Container>
    </Box>
  );
}

export default Hero2;
